import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PORTFOLIO = [
  {
    id: "p01",
    code: "P / 01",
    title: "Hawthorn Residence",
    tag: "Daylight Capture · Virtual Fit-out",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2070&auto=format&fit=crop",
  },
  {
    id: "p02",
    code: "P / 02",
    title: "Toorak Penthouse",
    tag: "Dusk Architectural · Matterport VR",
    image:
      "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2070&auto=format&fit=crop",
  },
  {
    id: "p03",
    code: "P / 03",
    title: "Carlton Heritage",
    tag: "Certified Floor Plans · Staging",
    image:
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=2071&auto=format&fit=crop",
  },
  {
    id: "p04",
    code: "P / 04",
    title: "Brighton Waterfront",
    tag: "Full Architectural Marketing Suite",
    image:
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2071&auto=format&fit=crop",
  },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  property_address: "",
  message: "",
};

const Landing = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, text: "" });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            // One-shot reveal for elegance — stop observing after it plays
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    const els = document.querySelectorAll(
      ".fade-in, .reveal-section, .hero-headline, .service-table tr, .portfolio-card"
    );
    els.forEach((el) => observer.observe(el));

    // Subtle architectural parallax on hero image
    const heroBg = document.querySelector(".hero-bg");
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (heroBg) {
          heroBg.style.transform = `translate3d(0, ${y * 0.18}px, 0) scale(1.05)`;
        }
        raf = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setStatus({ type: null, text: "" });

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({
        type: "error",
        text: "Name, email and message are required.",
      });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        property_address: form.property_address.trim() || null,
        message: form.message.trim(),
      });
      setStatus({
        type: "success",
        text: "Transmission received. We will respond within one business day.",
      });
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Unable to transmit. Please try again or email ravikumar@meanwhilearch.com.au directly.";
      setStatus({
        type: "error",
        text: typeof detail === "string" ? detail : "Unable to transmit.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="landing-page" className="ma-root">
      <div className="grid-overlay" />

      <nav data-testid="nav-bar">
        <div className="brand" data-testid="brand-logo">
          <img
            src={`${process.env.PUBLIC_URL}/brand/logo.png`}
            alt="meanwhileArch mark"
            className="brand-mark"
            data-testid="brand-mark"
          />
          <span className="brand-text">meanwhileArch</span>
        </div>
        <div className="tech-text" data-testid="system-status">
          <span className="status-dot" />
          <span className="system-status-text">System Active</span>
        </div>
      </nav>

      <section className="hero" data-testid="hero-section">
        <div className="hero-bg" />
        <div
          className="tech-text fade-in"
          style={{ marginBottom: "2rem" }}
          data-testid="hero-location"
        >
          Melbourne
        </div>
        <h1 className="hero-headline" data-testid="hero-headline">
          <span className="hl-line">
            <span className="hl-inner">Where raw</span>
          </span>
          <span className="hl-line">
            <span className="hl-inner">
              becomes <i className="shimmer">remarkable.</i>
            </span>
          </span>
        </h1>
        <p className="tech-text fade-in" data-testid="hero-subtitle">
          Architect-Led Visualization · Commercial Scale Precision
        </p>
      </section>

      <section className="usp-section reveal-section" id="about" data-testid="usp-section">
        <div className="usp-content fade-in">
          <h2 data-testid="usp-heading">
            Technical Exactness.<br />Not Clipart.
          </h2>
          <p data-testid="usp-paragraph-1">
            Standard virtual staging compromises the integrity of ultra-premium
            real estate. It scales incorrectly and interacts poorly with natural
            light.
          </p>
          <p data-testid="usp-paragraph-2">
            With 15 years of rigorous architectural experience, we engineer
            spatial visualizations that are mathematically certified. True
            commercial scale. Flawless structural lighting. Engineered for
            Melbourne&apos;s top-tier listings.
          </p>
        </div>
        <div className="usp-data fade-in">
          <table className="service-table" data-testid="service-table">
            <tbody>
              <tr data-testid="service-row-capture">
                <th>01 // CAPTURE</th>
                <td>Daylight &amp; Dusk Architectural Photography</td>
              </tr>
              <tr data-testid="service-row-spatial">
                <th>02 // SPATIAL</th>
                <td>Mathematically Certified 2D Floor Plans</td>
              </tr>
              <tr data-testid="service-row-staging">
                <th>03 // STAGING</th>
                <td>Architecturally Scaled Virtual Fit-outs</td>
              </tr>
              <tr data-testid="service-row-immersive">
                <th>04 // IMMERSIVE</th>
                <td>Matterport VR Walkthroughs</td>
              </tr>
              <tr data-testid="service-row-structural">
                <th>05 // STRUCTURAL</th>
                <td>Architect-Managed Construction &amp; Civil Execution</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        className="portfolio-section reveal-section"
        id="portfolio"
        data-testid="portfolio-section"
      >
        <div className="portfolio-header fade-in">
          <div className="tech-text amber-accent" style={{ marginBottom: "1rem" }}>
            Index · Selected Work
          </div>
          <h2 data-testid="portfolio-heading">
            Engineered for the<br />top of the market.
          </h2>
        </div>

        <div className="portfolio-grid" data-testid="portfolio-grid">
          {PORTFOLIO.map((item, idx) => (
            <article
              key={item.id}
              className={`portfolio-card fade-in ${idx % 2 === 1 ? "offset" : ""}`}
              data-testid={`portfolio-card-${item.id}`}
            >
              <div className="portfolio-frame">
                <img src={item.image} alt={item.title} loading="lazy" />
              </div>
              <div className="portfolio-meta">
                <div className="tech-text amber-accent">{item.code}</div>
                <h3>{item.title}</h3>
                <div className="tech-text">{item.tag}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        className="contact-section reveal-section"
        id="contact"
        data-testid="contact-section"
      >
        <div className="contact-intro fade-in">
          <div className="tech-text amber-accent" style={{ marginBottom: "1rem" }}>
            Phase 1: Network Debut
          </div>
          <h2 data-testid="contact-heading">
            Activate Pilot Program
          </h2>
          <p className="contact-copy" data-testid="contact-copy">
            Submit your listing for an architect-led visualization brief.
            Responses dispatched within one business day.
          </p>
          <div className="contact-direct tech-text">
            Direct ·{" "}
            <a
              href="mailto:ravikumar@meanwhilearch.com.au"
              className="amber-accent"
              data-testid="direct-email-link"
            >
              ravikumar@meanwhilearch.com.au
            </a>
          </div>
        </div>

        <form
          className="inquiry-form fade-in"
          onSubmit={handleSubmit}
          data-testid="inquiry-form"
          noValidate
        >
          <div className="field">
            <label htmlFor="name" className="tech-text">
              01 // Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              required
              autoComplete="name"
              data-testid="inquiry-input-name"
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="email" className="tech-text">
                02 // Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
                data-testid="inquiry-input-email"
              />
            </div>
            <div className="field">
              <label htmlFor="phone" className="tech-text">
                03 // Phone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                autoComplete="tel"
                data-testid="inquiry-input-phone"
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="property_address" className="tech-text">
              04 // Property Address
            </label>
            <input
              id="property_address"
              name="property_address"
              type="text"
              value={form.property_address}
              onChange={handleChange}
              autoComplete="street-address"
              data-testid="inquiry-input-address"
            />
          </div>

          <div className="field">
            <label htmlFor="message" className="tech-text">
              05 // Brief
            </label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              required
              data-testid="inquiry-input-message"
            />
          </div>

          {status.type && (
            <div
              className={`form-status ${status.type}`}
              data-testid={
                status.type === "success"
                  ? "inquiry-success"
                  : "inquiry-error"
              }
              role="status"
            >
              {status.text}
            </div>
          )}

          <button
            type="submit"
            className="btn"
            disabled={submitting}
            data-testid="inquiry-submit"
          >
            {submitting ? "Transmitting…" : "Request Consultation"}
          </button>
        </form>
      </section>

      <footer className="site-footer" data-testid="site-footer">
        <div className="footer-inner">
          <div className="tech-text">© {new Date().getFullYear()} meanwhileArch · Melbourne</div>
          <div className="tech-text amber-accent">Architect-Led Property Marketing</div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return <Landing />;
}

export default App;
